import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Heart, MessageCircle, ArrowLeft, Send, Loader2, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      toast.error('Post not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/api/posts/${postId}/like`);
      setPost({ ...post, likes_count: response.data.likes_count });
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/posts/${postId}/comments`, {
        content: comment
      });
      setPost({
        ...post,
        comments: [...(post.comments || []), response.data],
        comments_count: (post.comments_count || 0) + 1
      });
      setComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/api/posts/${postId}`);
      toast.success('Post deleted');
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        <div className="flex justify-center items-center pt-32">
          <Loader2 className="h-8 w-8 animate-spin text-[#6B8E7B]" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-20">
        <div className="max-w-2xl mx-auto py-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 rounded-full text-stone-600"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {/* Post */}
          <Card className="border-stone-200 rounded-2xl bg-white mb-6">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={post.author_avatar} />
                    <AvatarFallback className="bg-[#6B8E7B] text-white">
                      {getInitials(post.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-stone-800">{post.author_name}</p>
                    <div className="flex items-center gap-2 text-sm text-stone-500">
                      {post.author_specialization && (
                        <>
                          <span>{post.author_specialization}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {post.category && (
                    <Badge variant="outline" className="rounded-full text-xs font-normal text-stone-500 border-stone-200">
                      {post.category}
                    </Badge>
                  )}
                  {(user?.id === post.author_id || user?.role === 'admin') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDelete}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full"
                      data-testid="delete-post-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <h1 className="text-2xl font-bold text-stone-800 mb-4 font-['Outfit']">
                {post.title}
              </h1>
              <p className="text-stone-600 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </p>

              <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-100">
                <Button
                  variant="ghost"
                  onClick={handleLike}
                  className="rounded-full text-stone-500 hover:text-[#E27D60] hover:bg-[#E27D60]/10"
                  data-testid="post-like-button"
                >
                  <Heart className="h-5 w-5 mr-2" />
                  {post.likes_count || 0} likes
                </Button>
                <div className="flex items-center text-stone-500">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  {post.comments_count || 0} comments
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comment Form */}
          <Card className="border-stone-200 rounded-2xl bg-white mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleComment} className="flex gap-3">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 flex gap-2">
                  <Textarea
                    placeholder="Write a comment..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="rounded-xl resize-none min-h-[80px]"
                    data-testid="comment-input"
                  />
                  <Button
                    type="submit"
                    className="bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full self-end"
                    disabled={submitting || !comment.trim()}
                    data-testid="submit-comment-button"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Comments */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-stone-800 font-['Outfit']">
              Comments ({post.comments?.length || 0})
            </h3>
            
            {(!post.comments || post.comments.length === 0) ? (
              <Card className="border-stone-200 rounded-2xl bg-white">
                <CardContent className="p-8 text-center text-stone-500">
                  No comments yet. Be the first to comment!
                </CardContent>
              </Card>
            ) : (
              post.comments.map((comment, index) => (
                <Card key={comment.id || index} className="border-stone-200 rounded-2xl bg-white" data-testid={`comment-${comment.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <Avatar className="h-9 w-9 flex-shrink-0">
                        <AvatarFallback className="bg-[#6B8E7B] text-white text-xs">
                          {getInitials(comment.author_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-stone-800 text-sm">{comment.author_name}</span>
                          <span className="text-xs text-stone-400">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-stone-600 text-sm">{comment.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;
